import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import Accordion from "react-bootstrap/Accordion";
import Container from "react-bootstrap/Container";
import { Helmet } from "react-helmet-async";

import "../styles/q-and-a.css";
import { Api, PageSpinner } from "../util";
import { AppInvite } from "../types";
import { AuthContext } from "../context";
import CopyButton from "../components/CopyButton";
import MutedLink from "../components/MutedLink";

const AnswerAccordion = ({
  questions,
}: {
  questions: Record<string, React.ReactNode>;
}) => (
  <Accordion className="q-and-a pb-3" defaultActiveKey={["0"]}>
    {Object.entries(questions).map(([question, answer], i) => (
      <Accordion.Item eventKey={String(i)} key={i}>
        <Accordion.Header>{question}</Accordion.Header>
        <Accordion.Body>{answer}</Accordion.Body>
      </Accordion.Item>
    ))}
  </Accordion>
);

const QandA = () => {
  const [loading, setLoading] = React.useState(false);
  const [invites, setInvites] =
    React.useState<Array<AppInvite["event"]["slug"]>>();

  const { fetch } = React.useContext(AuthContext);

  const { hash } = useLocation();
  React.useLayoutEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [hash, invites]);

  React.useEffect(() => {
    if (fetch) {
      const api = new Api(fetch);
      void (async () => {
        setInvites((await api.getInvites()).map((invite) => invite.event.slug));
        setLoading(false);
      })();
    }
  }, [fetch]);

  if (loading || (fetch && !invites)) {
    return <PageSpinner />;
  }

  const generalQuestions = {
    "When should I RSVP by?": (
      <>
        We request that you RSVP as soon as possible so we have an accurate
        headcount for planning. The deadline to RSVP is March 15th, 2023.{" "}
        <Link className="link-secondary" to="/rsvp">
          RSVP here
        </Link>{" "}
        by selecting Accept/Decline for each guest and click Save. Every event
        has a separate RSVP so don&apos;t forget to click through each one.
      </>
    ),
    "What is the dress code for the events? What should I wear?": (
      <>
        The event-specific dress codes are listed in the{" "}
        <Link className="link-secondary" to="/schedule">
          Schedule
        </Link>
        . As a rule of thumb, Indian or Western formal is always appropriate.
      </>
    ),
    "What weather should I expect?": (
      <>
        Of course, we can&apos;t guarantee anything but May in Houston can get
        pretty warm, with chilly evenings. Keep an eye on the{" "}
        <MutedLink href="https://www.accuweather.com/en/us/houston/77002/">
          weather
        </MutedLink>{" "}
        and pack a light jacket if needed.
      </>
    ),
    "Can I bring my gift to the wedding/reception?": (
      <>
        Your presence is present enough, but should you wish to bring a gift to
        our wedding, we request that you opt for cash gifts or mail box gifts to
        our permanent residence in California. See the{" "}
        <Link className="link-secondary" target="_blank" to="/registry">
          Registry
        </Link>{" "}
        for more details.
      </>
    ),
    "Can I bring a +1?":
      "We have a fixed guest list. All events are RSVP-only and we will only able to accommodate those listed on your invitation.",
    "What should I do if I can't make it?": (
      <>
        You will be missed! If you can not make it to the wedding, please let us
        know as soon as possible and RSVP &quot;no&quot;, so we can plan
        accordingly.
      </>
    ),
    "I have a question not mentioned here. Who do I contact?": (
      <>
        Email us at{" "}
        <MutedLink href="mailto:snigdha.sushain@gmail.com">
          snigdha.sushain@gmail.com
        </MutedLink>
        .
      </>
    ),
  };

  const travelAccomodations = {
    "Do you have a hotel block for guests? Where do you recommend I stay?": (
      <>
        <p>
          We recommend staying near Cypress or Katy. We&apos;ve secured a hotel
          block at the{" "}
          <MutedLink href="https://goo.gl/maps/syRwNbFHmwTFSuKe6">
            SpringHill Suites Houston Northwest
          </MutedLink>
          . You&apos;ll be able to{" "}
          <MutedLink href="https://www.marriott.com/events/start.mi?id=1674659031643&key=GRP">
            book using the group rate
          </MutedLink>{" "}
          until May 1.
        </p>
        <p>
          We&apos;ve also reserved a block at the{" "}
          <MutedLink href="https://www.hilton.com/en/hotels/housuhx-hampton-suites-houston-sugar-land/">
            Hampton Inn & Suites Houston Sugar Land
          </MutedLink>
          , located closer to the reception venue (use group code{" "}
          <code>RCE</code> <CopyButton text="RCE" />
          ).
        </p>
        <p className="mb-0">
          The following alternative accomodations are all within 5 miles of the
          ceremony location:
          <ul className="mb-0">
            <li>
              <MutedLink href="https://goo.gl/maps/9KLbJVqgH2WozMDJ9">
                Hyatt Place
              </MutedLink>
            </li>
            <li>
              <MutedLink href="https://goo.gl/maps/THzxnEwtfVYQN5N46">
                Holiday Inn Express
              </MutedLink>
            </li>
          </ul>
        </p>
      </>
    ),
    "Which airport should I fly in to?":
      "IAH (George Bush Intercontinental Airport) or HOU (William P. Hobby Airport).",
    "How should I get to the venues? Is there easy parking available?": (
      <>
        <p>
          We recommend renting a car or using rideshare apps (Uber/Lyft) during
          your stay in Houston. Use{" "}
          <MutedLink href="https://www.budget.com/">Budget</MutedLink> Customer
          Discount (BCD) code <code>S444917</code> <CopyButton text="S444917" />{" "}
          or <MutedLink href="https://www.avis.com/">Avis</MutedLink> Customer
          Discount (AWD) code <code>J546139</code> <CopyButton text="J546139" />{" "}
          for discounted rates.
        </p>
        <p className="mb-0">
          There is ample free parking at all venues for those who feel
          comfortable driving. Please stay safe and default to a ride service or
          carpooling if you plan to drink.
        </p>
      </>
    ),
    "I'm visiting from out of town. What can I do while I'm in Houston?":
      "The NASA Space Center in Houston is a popular attraction. We also recommend checking out the Houston Galleria Mall and Discovery Green.",
  };

  const weddingQuestions = {
    "What time is the ceremony?": (
      <>
        The ceremony will begin at 2 PM. We ask that you arrive 15-30 minutes
        early to participate in the baraat (wedding procession) and take your
        seats in time for the bride&apos;s entrance. See the{" "}
        <Link className="link-secondary" to="/schedule">
          Schedule
        </Link>{" "}
        for more details.
      </>
    ),
    "What time should I arrive?":
      "Please be sure to arrive by the listed start time so we can stay on schedule for the planned events.",
    "Will the wedding be indoors or outdoors?":
      "The wedding ceremony and reception will be indoors, with an outdoor cocktail hour.",
    "What happens after the ceremony?":
      "The ceremony is followed by a cocktail hour with drinks and light refreshments, and beautiful outdoor photo opportunities. Reception doors will open at 6 PM and dinner will be served around 7 PM after the couple's first dance. See the Schedule page for more details.",
    "What type of food and drink will be served during the cocktail hour and reception?":
      (
        <>
          We&apos;ll be serving light snacks during cocktail hour, followed by a
          delicious dinner, and of course, cake! All meals will have both
          vegetarian and non-vegetarian options. If you have any specific
          dietary concerns,{" "}
          <a className="link-secondary" href="mailto:snigdha.sushain@gmail.com">
            let us know
          </a>
          .
        </>
      ),
  };

  const sangeetQuestions = {
    "What time is the Sangeet?": (
      <>
        The Sangeet will begin at 6:30 PM. Due to the number of performances, we
        ask that you arrive on time to not miss any of the entertainment.
      </>
    ),
    "What happens at a Sangeet?":
      "The Sangeet is the start to the wedding festivities, with music and dance performances from the couple's friends and family. The dance floor is open to all so bring your best moves!",
    "What type of food and drink will be served during the Sangeet?": (
      <>
        We will be serving a buffet dinner with both vegetarian and
        non-vegetarian options. If you have any specific dietary concerns,{" "}
        <a className="link-secondary" href="mailto:snigdha.sushain@gmail.com">
          let us know
        </a>
        .
      </>
    ),
  };

  return (
    <Container fluid="md">
      <Helmet>
        <title>Q&A</title>
      </Helmet>
      <div className="d-flex flex-column gap-3">
        <section>
          <h3 className="text-light pb-2" id="general">
            General
          </h3>
          <AnswerAccordion questions={generalQuestions} />
        </section>
        <section>
          <h3 className="text-light pb-2" id="travel-and-accomodations">
            Travel & Accomodations
          </h3>
          <AnswerAccordion questions={travelAccomodations} />
        </section>
        {invites?.includes("wedding") && (
          <section>
            <h3 className="text-light pb-2" id="wedding">
              Wedding
            </h3>
            <AnswerAccordion questions={weddingQuestions} />
          </section>
        )}
        {invites?.includes("sangeet") && (
          <section>
            <h3 className="text-light pb-2" id="sangeet">
              Sangeet
            </h3>
            <AnswerAccordion questions={sangeetQuestions} />
          </section>
        )}
      </div>
    </Container>
  );
};

export default QandA;
